// useLiveData.js — Données live SkiAlpPro partagées entre les pages React
// ---------------------------------------------------------------------------
// Porte les mêmes règles que live-ski.html : snapshot initial + WebSocket
// temps réel scopé par codex, sélection automatique de la manche en direct,
// reconstruction de l'heure réelle de départ (start_seconds_since_midnight).

import { useEffect, useRef, useState } from "react";

// --- Détection du codex depuis l'URL ---
// Fonctionne pour /live/CODEX, /live/CODEX/startlist, /live/CODEX/config, etc.
export function getCodexFromLocation() {
    const parts = window.location.pathname.split("/").filter(Boolean);
    const idx = parts.indexOf("live");
    if (idx !== -1 && parts[idx + 1]) return decodeURIComponent(parts[idx + 1]);
    return null;
}

// --- Hook principal : snapshot + WebSocket ---
export function useLiveData(codex) {
    const [data, setData] = useState({ heats: {}, timestamps: [], parameters: {} });
    const [connected, setConnected] = useState(false);

    useEffect(() => {
        if (!codex) return;
        let cancelled = false;
        let ws = null;
        let reconnectTimer = null;
        let attempt = 0;

        const fetchSnapshot = () => {
            fetch(`/api/live/${encodeURIComponent(codex)}`)
                .then((r) => (r.ok ? r.json() : null))
                .then((snapshot) => {
                    if (!cancelled && snapshot && snapshot.heats) setData(snapshot);
                })
                .catch(() => {});
        };

        const connect = () => {
            if (cancelled) return;
            const proto = window.location.protocol === "https:" ? "wss" : "ws";
            ws = new WebSocket(
                `${proto}://${window.location.host}/?codex=${encodeURIComponent(codex)}`
            );

            ws.onopen = () => {
                attempt = 0;
                setConnected(true);
                // On récupère aussi un état frais à chaque (re)connexion, au
                // cas où on aurait raté des messages pendant une coupure.
                fetchSnapshot();
            };

            ws.onclose = () => {
                setConnected(false);
                if (cancelled) return;
                // Reconnexion automatique avec backoff progressif (1s, 2s,
                // ... plafonné à 10s) — évite d'avoir à rafraîchir la page
                // manuellement après une coupure réseau ou un redémarrage
                // du serveur.
                attempt += 1;
                const delay = Math.min(1000 * attempt, 10000);
                reconnectTimer = setTimeout(connect, delay);
            };

            ws.onerror = () => {
                ws.close();
            };

            ws.onmessage = (event) => {
                let msg;
                try {
                    msg = JSON.parse(event.data);
                } catch {
                    return;
                }
                if (msg.type === "course_broadcast" && msg.data) {
                    setData(msg.data);
                }
            };
        };

        fetchSnapshot();
        connect();

        // Filet de sécurité : en plus du WebSocket, on re-vérifie l'état
        // toutes les 5s via un simple fetch. Ça couvre le cas où la
        // connexion temps réel reste ouverte mais cesse silencieusement de
        // recevoir des mises à jour (coupure réseau non détectée par le
        // navigateur) — sans recharger toute la page, donc sans perdre les
        // filtres, la vue sélectionnée ou le scroll.
        const pollTimer = setInterval(fetchSnapshot, 5000);

        return () => {
            cancelled = true;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            clearInterval(pollTimer);
            if (ws) ws.close();
        };
    }, [codex]);

    return { data, connected };
}

// --- Helpers de manches (mêmes règles que live-ski.html) ---

export function getRunning(competitors) {
    return (competitors || []).filter(
        (c) => c.status === "OK" && c.start_status === "STARTED" && !c.final_time
    );
}

export function heatHasLiveRunner(heatData) {
    return !!heatData && getRunning(heatData.competitors).length > 0;
}

// Choisit automatiquement quelle manche afficher : priorité à la manche la
// plus élevée avec un coureur en piste, sinon la manche la plus élevée
// ayant déjà des données.
export function pickAutoHeat(heats) {
    const keys = Object.keys(heats || {}).sort((a, b) => Number(a) - Number(b));
    for (let i = keys.length - 1; i >= 0; i--) {
        if (heatHasLiveRunner(heats[keys[i]])) return keys[i];
    }
    for (let i = keys.length - 1; i >= 0; i--) {
        if (heats[keys[i]].competitors && heats[keys[i]].competitors.length) return keys[i];
    }
    return keys[0] || null;
}

// --- Reconstitution de l'heure réelle de départ (GMT+2, comme live-ski.html) ---
const LOCAL_OFFSET_MS = 2 * 3600 * 1000;

function localMidnightEpochMs(referenceMs) {
    const shifted = new Date(referenceMs + LOCAL_OFFSET_MS);
    const y = shifted.getUTCFullYear();
    const m = shifted.getUTCMonth();
    const d = shifted.getUTCDate();
    const shiftedMidnightUTC = Date.UTC(y, m, d, 0, 0, 0, 0);
    return shiftedMidnightUTC - LOCAL_OFFSET_MS;
}

export function startSecondsToClientMs(startSecondsSinceMidnight) {
    if (startSecondsSinceMidnight == null) return null;
    const now = Date.now();
    const midnight = localMidnightEpochMs(now);
    let ts = midnight + startSecondsSinceMidnight * 1000;
    const DAY = 24 * 3600 * 1000;
    if (ts - now > DAY / 2) ts -= DAY;
    if (now - ts > DAY / 2) ts += DAY;
    return ts;
}

// Convertit un temps (en secondes) en "H:MM:SS.ss" (ou "M:SS.ss" si < 1h).
export function formatDuration(seconds) {
    if (seconds == null || Number.isNaN(Number(seconds))) return "";
    const totalCentis = Math.round(Number(seconds) * 100);
    const cs = totalCentis % 100;
    const totalSec = Math.floor(totalCentis / 100);
    const s = totalSec % 60;
    const totalMin = Math.floor(totalSec / 60);
    const m = totalMin % 60;
    const h = Math.floor(totalMin / 60);
    const pad = (n) => String(n).padStart(2, "0");
    const secStr = `${pad(s)}.${pad(cs)}`;
    return h > 0 ? `${h}:${pad(m)}:${secStr}` : `${m}:${secStr}`;
}

// Petits temps (pénalités gates/jump) : pas besoin du format H:MM:SS,
// quelques secondes suffisent.
export function formatPenalty(seconds) {
    if (!seconds) return "";
    return Number(seconds).toFixed(2);
}

// C_SEX (TCOMPETITORS) : 1 = homme, 2 = femme.
export function sexLabel(code) {
    const v = Number(code);
    if (v === 1) return "Homme";
    if (v === 2) return "Femme";
    return "";
}

// DISCIPLINE_TYPE = 20 dans TPARAMETERS -> course de télémark (inters +
// pénalités gates/jump pertinents à afficher).
export function isTelemarkDiscipline(parameters) {
    return String(parameters?.DISCIPLINE_TYPE ?? "") === "20";
}

// Nombre d'intermédiaires configurés pour l'évènement (TPARAMETERS.INTER_NUMBER).
export function getInterCount(parameters) {
    const n = Number(parameters?.INTER_NUMBER);
    return Number.isFinite(n) && n > 0 ? n : 0;
}

// Catégories dynamiques configurées par l'organisateur (Config.jsx),
// stockées dans event_config sous forme de tableau :
// [{ name, sex: "ALL"|"HOMME"|"FEMME", yearMin, yearMax }, ...]
// Un compétiteur matche une catégorie si son année de naissance est dans
// l'intervalle (bornes optionnelles) et si son sexe correspond (si filtré).
export function matchesCategory(competitorYear, competitorSexCode, category) {
    if (!category) return true;

    const year = Number(competitorYear);
    if (category.yearMin !== "" && category.yearMin != null) {
        if (!Number.isFinite(year) || year < Number(category.yearMin)) return false;
    }
    if (category.yearMax !== "" && category.yearMax != null) {
        if (!Number.isFinite(year) || year > Number(category.yearMax)) return false;
    }

    if (category.sex && category.sex !== "ALL") {
        const wanted = category.sex === "HOMME" ? 1 : category.sex === "FEMME" ? 2 : null;
        if (wanted != null && Number(competitorSexCode) !== wanted) return false;
    }

    return true;
}

// Calcule, pour chaque dossard classé (status OK + final_time), son rang et
// son écart AU SEIN de sa catégorie (pas le rang scratch). Un coureur qui
// matche plusieurs catégories garde la première (ordre de définition dans
// Config.jsx). Renvoie { [bib]: { categoryName, catRank, catGap } }.
export function computeCategoryRankings(rankedCompetitors, categories) {
    const result = {};
    if (!categories || categories.length === 0) return result;

    categories.forEach((cat) => {
        const members = rankedCompetitors
            .filter((c) => c.final_time != null && matchesCategory(c.year, c.sex, cat))
            .slice()
            .sort((a, b) => a.final_time - b.final_time);

        if (members.length === 0) return;

        const best = members[0].final_time;
        members.forEach((c, i) => {
            if (!result[c.bib]) {
                result[c.bib] = {
                    categoryName: cat.name || "",
                    catRank: i + 1,
                    catGap: c.final_time - best,
                };
            }
        });
    });

    return result;
}

// Convertit un DATE "serial" (type Excel/Lotus, jours depuis 1899-12-30),
// tel qu'utilisé dans TPARAMETERS.DATE, en date lisible (fr-CH).
export function excelSerialToDateString(serial) {
    if (!serial) return "";
    const ms = Date.UTC(1899, 11, 30) + Number(serial) * 86400000;
    return new Date(ms).toLocaleDateString("fr-CH", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

// --- Chrono "en piste" par manche : startedAt persistant par bib, tant que ---
// le coureur reste en piste dans CETTE manche (jamais réinitialisé en bloc).
// Re-render toutes les 100ms pour un affichage fluide.
export function useHeatRunners(heats, effectiveHeat) {
    const storeRef = useRef({}); // heat -> { bib: startedAtMs }
    const [, forceTick] = useState(0);

    useEffect(() => {
        const id = setInterval(() => forceTick((t) => t + 1), 100);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        if (!effectiveHeat || !heats[effectiveHeat]) return;
        if (!storeRef.current[effectiveHeat]) storeRef.current[effectiveHeat] = {};
        const heatRunners = storeRef.current[effectiveHeat];

        const running = getRunning(heats[effectiveHeat].competitors);
        const runningBibs = new Set(running.map((c) => String(c.bib)));

        running.forEach((c) => {
            if (!heatRunners[c.bib]) {
                const real = startSecondsToClientMs(c.start_seconds_since_midnight);
                heatRunners[c.bib] = real != null ? real : Date.now();
            }
        });

        Object.keys(heatRunners).forEach((bib) => {
            if (!runningBibs.has(String(bib))) delete heatRunners[bib];
        });
    }, [heats, effectiveHeat]);

    const currentMap = storeRef.current[effectiveHeat] || {};
    const now = Date.now();
    const elapsedByBib = {};
    Object.keys(currentMap).forEach((bib) => {
        elapsedByBib[bib] = (now - currentMap[bib]) / 1000;
    });
    return elapsedByBib;
}